import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Redux.
import { connect } from 'react-redux';

// Components.
import TrackInfoRow from './TrackInfoRow';
import AudioPlayerDrawer from './AudioPlayerDrawer';
import { groupTrackInfosByPublishedDate } from './timeHelper';
import { loadTrack } from './audioHelper';
import TrackInfoRowsLoading from './TrackInfoRowsLoading';
import LibraryFollowingIntro from './LibraryFollowingIntro';
import { loadLatestSubscribed } from '../redux/actions/userActions';
import FeedRefreshNotice from './FeedRefreshNotice';
import { followingFeedIdentity } from '../utils/followingFeedIdentity';
import './SubscribedLatestEpisodes.css';


// The episode list skips null entries, so they must not affect refresh comparisons.
const episodeIds = data => (data?.trackInfos || []).filter(Boolean).map(track => track.id);

const hasSameEpisodeOrder = (first, second) => {
    const firstIds = episodeIds(first);
    const secondIds = episodeIds(second);
    return firstIds.length === secondIds.length &&
        firstIds.every((id, index) => id === secondIds[index]);
};

// Account changes discard local staging.
export const SubscribedLatestEpisodes = props => <FollowingEpisodes key={props.uid || 'signed-out'} {...props} />;

const FollowingEpisodes = ({
    // Redux.
    authenticated,
    uid,
    speakerSubscribeIds = [],
    podcastSubscribeIds = [],
    latestSubscribedData,
    latestSubscribedLoading,
    latestSubscribedRefreshReason = 'automatic',
    latestSubscribedError,
    latestSubscribedInvalidationRevision = 0,
    loadLatestSubscribed,
    deferAutomaticRefresh = false,
    followSavesPending = false,
}) => {
    const [showAudioPlayerDrawer, setShowAudioPlayerDrawer] = useState(false);
    const [isOnline, setIsOnline] = useState(() => navigator.onLine !== false);
    const trackInfoRef = useRef(null);
    const hasFollows = speakerSubscribeIds.length > 0 || podcastSubscribeIds.length > 0;
    const currentFollowIdentity = followingFeedIdentity(speakerSubscribeIds, podcastSubscribeIds);
    const followIdentity = latestSubscribedData?.followIdentity ?? currentFollowIdentity;
    const [feed, setFeed] = useState(() => ({
        data: latestSubscribedData,
        hasFollows,
        followIdentity,
        // An invalidated cache still belongs to the previous set of follows.
        revision: latestSubscribedData?.setTime === 0 ? null : latestSubscribedInvalidationRevision,
        pending: null,
    }));

    // Redux remains the cache; this mounted view holds its ordering until Show.
    // Invalidating a cache only sets setTime to zero and is not a new result.
    useEffect(() => {
        if (!latestSubscribedData || latestSubscribedData.setTime === 0) return;
        setFeed(previous => {
            if (previous.data === latestSubscribedData) return previous;
            if (previous.data && (deferAutomaticRefresh || followSavesPending)) return previous;
            if (!episodeIds(previous.data).length || hasSameEpisodeOrder(previous.data, latestSubscribedData)) {
                return { data: latestSubscribedData, hasFollows, followIdentity,
                    revision: latestSubscribedInvalidationRevision, pending: null };
            }
            const visibleIds = new Set(episodeIds(previous.data));
            return { ...previous, pending: {
                data: latestSubscribedData,
                hasFollows,
                followIdentity,
                revision: latestSubscribedInvalidationRevision,
                hasNewEpisodes: previous.revision === latestSubscribedInvalidationRevision &&
                    previous.followIdentity === followIdentity &&
                    episodeIds(latestSubscribedData).some(id => !visibleIds.has(id)),
            } };
        });
    }, [latestSubscribedData, latestSubscribedInvalidationRevision, hasFollows, followIdentity,
        deferAutomaticRefresh, followSavesPending]);

    const displayData = feed.data || latestSubscribedData;
    const latestTrackInfos = useMemo(
        () => displayData && Array.isArray(displayData.trackInfos) ?
            displayData.trackInfos :
            [],
        [displayData],
    );
    const trackInfoGroups = useMemo(
        () => groupTrackInfosByPublishedDate(latestTrackInfos),
        [latestTrackInfos],
    );

    // A follow can invalidate the first request before it returns. Once saves
    // finish, allow that initial load so there is a feed to hold in place.
    const shouldDeferRefresh = deferAutomaticRefresh && (followSavesPending || !!displayData);
    const refreshIfNeeded = useCallback(() => {
        if (authenticated && !shouldDeferRefresh) loadLatestSubscribed();
    }, [authenticated, shouldDeferRefresh, loadLatestSubscribed]);

    const pending = feed.pending?.revision === latestSubscribedInvalidationRevision ? feed.pending : null;
    const showUpdates = () => {
        if (!pending || followSavesPending || deferAutomaticRefresh) return;
        const { data, hasFollows, followIdentity, revision } = pending;
        setFeed({ data, hasFollows, followIdentity, revision, pending: null });
    };

    let noticeState = null;
    if (isOnline && !deferAutomaticRefresh && !followSavesPending) {
        if (latestSubscribedLoading && latestSubscribedRefreshReason !== 'automatic') {
            noticeState = displayData ? 'refreshing' : null;
        }
        else if (pending) noticeState = 'ready';
        else if (latestSubscribedError) noticeState = 'failed';
    }

    useEffect(() => {
        refreshIfNeeded();
    }, [refreshIfNeeded, latestSubscribedInvalidationRevision]);

    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === 'visible') refreshIfNeeded();
        };
        const onOnline = () => { setIsOnline(true); refreshIfNeeded(); };
        const onOffline = () => setIsOnline(false);
        window.addEventListener('online', onOnline);
        window.addEventListener('offline', onOffline);
        window.addEventListener('focus', refreshIfNeeded);
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            window.removeEventListener('online', onOnline);
            window.removeEventListener('offline', onOffline);
            window.removeEventListener('focus', refreshIfNeeded);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [refreshIfNeeded]);

    const playTrack = (trackInfo, startTime) => () => {
        trackInfoRef.current = trackInfo;
        loadTrack(trackInfo.mp3URL, startTime);
        setShowAudioPlayerDrawer(true);
    };

    const closeAudioPlayer = () => {
        setShowAudioPlayerDrawer(false);
    }


    return (
        <div className={trackInfoGroups.length > 0 ? 'followingFeedHasEpisodes' : undefined}>
            <AudioPlayerDrawer
                open={showAudioPlayerDrawer}
                onClose={closeAudioPlayer}
                preservePlaybackOnClose
                source={trackInfoRef.current ? trackInfoRef.current.source : null}
                trackId={trackInfoRef.current ? trackInfoRef.current.id : null} />
            <div className='followingFeedHeader'>
                <h2 className='libraryFollowingHeading'>Following</h2>
            </div>
            {noticeState && <FeedRefreshNotice state={noticeState}
                updatingLabel={latestSubscribedRefreshReason === 'manual' ? 'Checking for new episodes…' : 'Updating your feed…'}
                readyLabel={pending?.hasNewEpisodes && pending.followIdentity === currentFollowIdentity
                    ? 'New episodes' : 'Updates available'}
                onShow={showUpdates} onRetry={() => loadLatestSubscribed({ force: true })} />}
            {displayData && !latestSubscribedError && isOnline && (
                <LibraryFollowingIntro
                    uid={uid}
                    hasEpisodes={trackInfoGroups.length > 0}
                    hasFollows={feed.hasFollows}
                />
            )}
            {!isOnline && trackInfoGroups.length === 0 && (
                <p className='followingFeedConnectionMessage'>Connect to load new episodes.</p>
            )}
            {latestSubscribedLoading && !displayData && isOnline ? (
                <TrackInfoRowsLoading />
            ) : trackInfoGroups.length > 0 ? (
                <div className='libraryTrackInfoGroups'>
                    {trackInfoGroups.map((group) => (
                        <div key={group.id} className='libraryTrackInfoGroup'>
                            <div className='libraryTrackInfoGroupLabel'>
                                <span className='libraryTrackInfoGroupLabelText'>{group.title}</span>
                                <span className='libraryTrackInfoGroupLabelLine' />
                            </div>
                            <div className='trackInfoRowsVerticalList'>
                                {group.trackInfos.map((track, index) => (
                                    <TrackInfoRow
                                        key={track && track.id ? track.id : `${group.id}-${index}`}
                                        trackInfo={track}
                                        playTrack={playTrack}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

const mapStateToProps = (state) => ({
    authenticated: state.user.authenticated,
    uid: state.user.uid,
    speakerSubscribeIds: state.user.speakerSubscribeIds,
    podcastSubscribeIds: state.user.podcastSubscribeIds,
    latestSubscribedData: state.user.latestSubscribedData,
    latestSubscribedLoading: state.user.latestSubscribedLoading,
    latestSubscribedRefreshReason: state.user.latestSubscribedRefreshReason,
    latestSubscribedError: state.user.latestSubscribedError,
    latestSubscribedInvalidationRevision: state.user.latestSubscribedInvalidationRevision,
})

const mapActionsToProps = {
    loadLatestSubscribed,
}

export default connect(mapStateToProps, mapActionsToProps)(SubscribedLatestEpisodes);
